<template>
  <select-cifra
    v-model="localValue"
    :options="usuarios"
    :loading="loading"
    :placeholder="placeholder"
    :multiple="multiple"
    :show-labels="showLabels"
    label="fullname"
    track-by="id"
    @search-change="onSearch"
  >
    <template #option="props">
      <span
        v-if="icon"
        :class="props.option.icon.icono"
        data-toggle="tooltip"
        data-placement="left"
        :title="props.option.icon.title"
      ></span>
      {{ getOptionLabel(props.option) }}
      <span
        v-if="props.option.contador"
        class="label label-info pull-right"
        data-toggle="tooltip"
        :title="props.option.tooltipContador"
      >
        {{ props.option.contador }}
      </span>
    </template>
  </select-cifra>
</template>

<script>
import { inject } from 'vue';
import { debounce, uniqBy } from 'lodash';
import { usuarios } from 'cifra-api-js';

export default {
  props: {
    modelValue: { type: [Number, String, Array], default: undefined },
    placeholder: {
      type: String,
      default() {
        const I18n = inject('I18n');
        return I18n.t('introduzca_usuario');
      },
    },
    multiple: { type: Boolean, default: false },
    fullNameFormat: { type: Boolean, default: true },
    icon: { type: Boolean, default: false },
    // Muestra los textos a la derecha del option "click para seleccionar"
    showLabels: { type: Boolean, default: false },
    // profesor, alumno, padre
    tipo: { type: String, default: null },
    activos: { type: Boolean, default: null },
    precargar: { type: Boolean, default: false },
    // Debe ser un array de objetos usuario
    usuariosFijos: { type: Array, default: null },
  },
  emits: ['update:modelValue', 'usuarios-preparados'],
  data() {
    return {
      usuarios: this.prepararUsuariosIniciales(),
      localValue: null,
      loading: false,
    };
  },
  computed: {
    usuarioActual() {
      return this.localValue;
    },
  },
  watch: {
    // Los valores que devuelve el multiselect son objetos de tipo usuario, aquí
    // mapeamos a enteros
    localValue(value) {
      let nuevo = value;
      if (Array.isArray(nuevo)) {
        nuevo = value.map((val) => val.id);
      } else if (value instanceof Object) {
        nuevo = nuevo.id;
      }

      // Cambiamos nosotros el valor, hacemos que el watch de value
      // lo omita la gestión
      this.omitirSiguiente = true;
      this.$emit('update:modelValue', nuevo);
    },
    // Los valores de este componente son integer, si cambia desde fuera
    // o hay valor inicial, cargamos el objeto del servidor
    modelValue(value) {
      if (this.omitirSiguiente) {
        this.omitirSiguiente = false;
        return;
      }

      this._cargarUsuariosById(value);
    },
  },
  mounted() {
    this.omitirSiguiente = false;
    if (this.precargar) {
      this.ajaxSearch(null, this);
    } else {
      this._cargarUsuariosById(this.modelValue);
    }
  },
  methods: {
    onSearch(texto) {
      // Si son usuarios fijos, no buscamos con ajax
      if (this.usuariosFijos) {
        return;
      }
      if (texto.length) {
        // Si está precargado ya filtra el componente, no llamamos más ajax
        if (!this.precargar) {
          this.loading = true;
          this.ajaxSearch(texto, this);
        }
      }
    },
    prepararUsuarios(users) {
      let usuariosFiltrados = users.filter((user) => user);
      usuariosFiltrados = uniqBy(usuariosFiltrados, 'id');
      usuariosFiltrados.forEach((usuario) => {
        usuario.fullname = usuarios.fullname(usuario, this.fullNameFormat);
        if (this.icon) {
          usuario.icon = this.usuarioIcono(usuario);
        }
      });
      this.$emit('usuarios-preparados', usuariosFiltrados);
      return usuariosFiltrados;
    },
    prepararUsuariosIniciales() {
      if (!this.usuariosFijos) {
        return [];
      }

      let resultado;
      const nombreGrupo = this.$attrs['group-values'];
      // Si está agrupado, cogemos los valores del grupo
      if (nombreGrupo) {
        resultado = this.usuariosFijos.map((grupo) => {
          grupo[nombreGrupo] = this.prepararUsuarios(grupo[nombreGrupo]);
          return grupo;
        });
      } else {
        resultado = this.prepararUsuarios(this.usuariosFijos);
      }
      return resultado;
    },
    usuarioIcono(usuario) {
      let icono = 'fa fa-user fa-blank';
      let title = '';
      if (usuario.caracteristicas.alumno) {
        icono = 'fa fa-user text-alumno';
        title = this.$I18n.t('Alumno');
      } else if (usuario.caracteristicas.profesor) {
        icono = 'fa fa-user-graduate text-profesor';
        title = this.$I18n.t('Profesor');
      } else if (usuario.caracteristicas.padre) {
        icono = 'fa fa-user-tie text-padre';
        title = this.$I18n.t('Padre');
      }
      return { icono, title };
    },
    ajaxSearch: debounce((texto, vm) => {
      const params = { q: texto };
      if (vm.tipo) {
        params[vm.tipo] = true;
      }
      if (vm.activos !== null) {
        params.activo = vm.activos;
      }
      usuarios.list({ params }).then((result) => {
        vm.usuarios = vm.prepararUsuarios(result.data);
        vm.loading = false;
      }).catch((_result) => {
        vm.loading = false;
      });
    }, 350),
    addUsuarios(users) {
      this.localValue = this.localValue || [];
      this.localValue = this.prepararUsuarios(this.localValue.concat(users));
    },
    // Traído de https://github.com/shentao/vue-multiselect/blob/ee7bf2170e591fe55caa31d1a9aeb7d90993e7e5/src/multiselectMixin.js#L28
    // para pintar bien si es una opción o un tag o un optgroup
    getOptionLabel(option) {
      if (!option) return '';
      if (option.isTag) return option.label;
      if (option.$isLabel) return option.$groupLabel;

      return option.fullname;
    },
    // Nos pasan el id o los ids de los usuarios que tenemos que seleccionar
    _cargarUsuariosById(usuarioIds) {
      if (!usuarioIds) {
        return;
      }
      usuarios.list({ params: { id: usuarioIds } }).then((result) => {
        const lista = this.prepararUsuarios(result.data);
        if (this.multiple) {
          this.localValue = lista;
        } else {
          this.localValue = lista.length > 0 ? lista[0] : null;
        }
      });
    },
  },
};
</script>

<style scoped>
.multiselect__option .fa {
  margin-right: 5px;
}
</style>
